/**
 * Utility for "Intent" columns in TipTap tables.
 *
 * Detects cells whose column header text is "Intent" and attaches
 * a click handler that opens a modal overlay with a rich TipTap editor
 * supporting Markdown and @ mentions.
 */
import { useCallback, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import type { Editor } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Markdown } from "@tiptap/markdown";
import { DOMParser as PMDOMParser, DOMSerializer } from "prosemirror-model";
import {
  AvatarMention,
  DocumentMention,
  createMentionSuggestionOptions,
  MentionUser,
  MentionDocument
} from "./MentionSuggestion";

const MODAL_OVERLAY_CLASS = "intent-modal-overlay";

/**
 * Resolve the header text for a given column index by walking
 * up the ProseMirror document tree to the containing table.
 */
function getColumnHeaderText(
  editor: Editor,
  pos: number,
  columnIndex: number
): string | null {
  const $pos = editor.state.doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === "table") {
      const firstRow = node.firstChild;
      if (
        firstRow?.type.name === "tableRow" &&
        columnIndex < firstRow.childCount
      ) {
        return firstRow.child(columnIndex).textContent;
      }
      break;
    }
  }
  return null;
}

/**
 * Check whether a cell at the given position is in an "Intent" column.
 */
export function isIntentColumn(
  editor: Editor,
  pos: number | null | undefined,
  columnIndex: number
): boolean {
  if (pos == null || columnIndex < 0) return false;
  const headerText = getColumnHeaderText(editor, pos, columnIndex);
  return headerText?.trim().toLowerCase() === "intent";
}

// ─── Modal React component with TipTap editor ───────────────────────────────

interface IntentModalProps {
  html: string;
  editable: boolean;
  mentionUsers: MentionUser[];
  mentionDocuments: MentionDocument[];
  onSave?: (html: string) => void;
  onClose: () => void;
}

function IntentModal({
  html,
  editable,
  mentionUsers,
  mentionDocuments,
  onSave,
  onClose
}: IntentModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  const modalEditor = useEditor({
    extensions: [
      StarterKit.configure({ undoRedo: false }),
      Markdown,
      Image,
      AvatarMention.configure({
        HTMLAttributes: { class: "mention" },
        suggestion: createMentionSuggestionOptions(
          mentionUsers,
          mentionDocuments
        )
      }),
      DocumentMention.configure({
        HTMLAttributes: { class: "mention-document" }
      })
    ],
    editable,
    content: html || ""
  });

  const handleSave = useCallback(() => {
    if (!modalEditor || !onSave) return;
    onSave(modalEditor.getHTML());
    onClose();
  }, [modalEditor, onSave, onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (editable && modalEditor) {
      modalEditor.commands.focus("end");
    }
  }, [editable, modalEditor]);

  return (
    <div
      ref={overlayRef}
      className={MODAL_OVERLAY_CLASS}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="intent-modal-content">
        <div className="intent-modal-header">
          <span>Intent</span>
          <button
            type="button"
            className="intent-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="intent-modal-editor">
          <EditorContent editor={modalEditor} />
        </div>

        {editable && onSave && (
          <div className="intent-modal-footer">
            <button
              type="button"
              className="intent-modal-save"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Imperative modal opener ─────────────────────────────────────────────────

interface IntentModalOptions {
  html: string;
  editable?: boolean;
  mentionUsers?: MentionUser[];
  mentionDocuments?: MentionDocument[];
  onSave?: (html: string) => void;
}

function openIntentModal({
  html,
  editable = false,
  mentionUsers = [],
  mentionDocuments = [],
  onSave
}: IntentModalOptions): void {
  // Prevent duplicate modals
  if (document.querySelector(`.${MODAL_OVERLAY_CLASS}`)) return;

  const container = document.createElement("div");
  document.body.appendChild(container);

  const root = createRoot(container);

  const close = () => {
    root.unmount();
    container.remove();
  };

  root.render(
    <IntentModal
      html={html}
      editable={editable}
      mentionUsers={mentionUsers}
      mentionDocuments={mentionDocuments}
      onSave={onSave}
      onClose={close}
    />
  );
}

// ─── Node view integration ───────────────────────────────────────────────────

export interface ApplyIntentOptions {
  editable?: boolean;
  getPos?: () => number | null | undefined;
  mentionUsers?: MentionUser[];
  mentionDocuments?: MentionDocument[];
}

const TRUNCATE_LIMIT = 100;
const SHOW_MORE_CLASS = "intent-show-more";

export interface IntentNodeViewSpec {
  dom: HTMLElement;
  contentDOM: HTMLElement;
  update: () => boolean;
  ignoreMutation: (
    mutation: MutationRecord | { type: "selection"; target: Node }
  ) => boolean;
  destroy: () => void;
}

/**
 * Apply Intent-column click-to-modal behavior to a table cell.
 *
 * Returns a node view spec if the cell is in an Intent column, or null otherwise.
 */
export function applyIntentColumnModal(
  dom: HTMLElement,
  contentDOM: HTMLElement,
  editor: Editor,
  pos: number | null | undefined,
  columnIndex: number,
  options?: ApplyIntentOptions
): IntentNodeViewSpec | null {
  if (!isIntentColumn(editor, pos, columnIndex)) return null;

  dom.classList.add("intent-cell");
  dom.appendChild(contentDOM);

  const showMore = document.createElement("span");
  showMore.className = SHOW_MORE_CLASS;
  showMore.textContent = "Show more...";
  showMore.style.display = "none";
  dom.appendChild(showMore);

  const updateTruncation = () => {
    const text = contentDOM.textContent || "";
    if (text.length > TRUNCATE_LIMIT) {
      dom.classList.add("intent-cell-truncated");
      showMore.style.display = "";
    } else {
      dom.classList.remove("intent-cell-truncated");
      showMore.style.display = "none";
    }
  };

  let rafId: number | null = null;
  const scheduleUpdate = () => {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      updateTruncation();
    });
  };

  // Watch for content changes (ProseMirror / Yjs populate contentDOM after creation)
  const observer = new MutationObserver(scheduleUpdate);
  observer.observe(contentDOM, {
    childList: true,
    subtree: true,
    characterData: true
  });

  scheduleUpdate();

  const handleClick = () => {
    const isEditable = options?.editable ?? false;

    // Serialize the cell's ProseMirror content to semantic HTML
    // (contentDOM.innerHTML would capture node-view visuals, not parseable markup)
    let cellHtml = "";
    const currentClickPos =
      typeof options?.getPos === "function" ? options.getPos() : pos;
    if (currentClickPos != null) {
      const cellNode = editor.state.doc.nodeAt(currentClickPos);
      if (cellNode) {
        const serializer = DOMSerializer.fromSchema(editor.schema);
        const fragment = serializer.serializeFragment(cellNode.content);
        const wrapper = document.createElement("div");
        wrapper.appendChild(fragment);
        cellHtml = wrapper.innerHTML;
      }
    }

    openIntentModal({
      html: cellHtml,
      editable: isEditable,
      mentionUsers: options?.mentionUsers ?? [],
      mentionDocuments: options?.mentionDocuments ?? [],
      onSave: isEditable
        ? (newHtml: string) => {
            // Get fresh position in case it shifted
            const currentPos =
              typeof options?.getPos === "function" ? options.getPos() : pos;
            if (currentPos == null) return;

            const cellNode = editor.state.doc.nodeAt(currentPos);
            if (!cellNode) return;

            // Parse the modal editor's HTML back into ProseMirror nodes
            const tempEl = document.createElement("div");
            tempEl.innerHTML = newHtml;
            const parsed = PMDOMParser.fromSchema(editor.schema).parse(tempEl);

            const tr = editor.state.tr;
            tr.replaceWith(
              currentPos + 1,
              currentPos + cellNode.nodeSize - 1,
              parsed.content
            );
            editor.view.dispatch(tr);
          }
        : undefined
    });
  };

  dom.addEventListener("click", handleClick);

  return {
    dom,
    contentDOM,
    update() {
      scheduleUpdate();
      return true;
    },
    ignoreMutation(
      mutation: MutationRecord | { type: "selection"; target: Node }
    ) {
      const target = mutation.target as Node | null;
      // Let ProseMirror handle changes inside the editable content
      if (target && contentDOM.contains(target)) {
        return false;
      }
      // Ignore mutations on our showMore/class changes to avoid re-render loops
      return true;
    },
    destroy() {
      observer.disconnect();
      dom.removeEventListener("click", handleClick);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    }
  };
}
